import { FormInput, FormSelect, PaymentProcessorEnum, PrimaryButton, getPaymentProcessorTitle, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import { InternalError } from '@systemeio/util';
import HelpCenterLink from 'modules/profile/payment-gateways/components/help-center-link';
import { useConnectedPaymentIntegrations } from 'modules/profile/payment-gateways/hooks/use-connected-payment-integrations';
import { getPaystackSubAccounts, paystackConnect } from 'modules/profile/payment-gateways/integrations/paystack/api';
import { PaystackCreateIntegrationInterface, PaystackIntegrationErrorsInterface } from 'modules/profile/payment-gateways/integrations/paystack/types';
import { getPaystackHelpPageLink, getSubAccountData } from 'modules/profile/payment-gateways/integrations/paystack/utils';
import React, { useEffect, useRef, useState } from 'react';
import CreateModal from 'shared/components/create-modal';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import useUser from 'shared/hooks/use-user';
import { fetchWithErrors } from 'shared/utils/fetch-with-errors';
const defaultValues: PaystackCreateIntegrationInterface = {
  secretToken: '',
  subaccountCode: undefined
};
const defaultErrors: PaystackIntegrationErrorsInterface = {
  secretToken: '',
  subaccountCode: ''
};
const PaystackCreateModal = () => {
  const {
    t
  } = useLocoTranslation();
  const {
    user
  } = useUser();
  const [opened, setOpened] = useState(false);
  const [errors, setErrors] = useState(defaultErrors);
  const [tempSecretKey, setTempSecretKey] = useState('');
  const [subAccountSelectData, setSubAccountSelectData] = useState<{
    id: string;
    caption: string;
  }[] | undefined>([]);
  const [isSubAccountsFetching, setIsSubAccountsFetching] = useState(false);
  const abortController = useRef<AbortController | null>(null);
  const {
    mutatePaymentIntegrations
  } = useConnectedPaymentIntegrations();
  const onCreate = async (data: PaystackCreateIntegrationInterface) => {
    await fetchWithErrors(async () => {
      await paystackConnect(data);
      await mutatePaymentIntegrations();
    }, setErrors);
  };
  const cancelRequest = () => abortController.current && abortController.current.abort();
  useEffect(() => {
    cancelRequest();
    abortController.current = new AbortController();
    const getSubaccountsData = setTimeout(async () => {
      if (!!tempSecretKey) {
        try {
          setIsSubAccountsFetching(true);
          const response = await getPaystackSubAccounts({
            secretToken: tempSecretKey
          }, {
            signal: abortController.current?.signal
          });
          setSubAccountSelectData(getSubAccountData(response.data));
          setIsSubAccountsFetching(false);
        } catch (error) {
          setIsSubAccountsFetching(false);
          if (error instanceof BadRequest) {
            toast.error(t('global.invalid_credentials'));
          } else if (error instanceof InternalError) {
            toast.error(t('core.error.internal_error_message'));
          } else {
            toast.error(t('global.error'));
          }
        }
      }
    }, 1000);
    return () => clearTimeout(getSubaccountsData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tempSecretKey]);
  return <>
      <PrimaryButton onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setOpened(true);
    }} className={'font-bold'}>
        {t('dashboard.settings.payment_gateways.connect')}
      </PrimaryButton>
      <CreateModal fullCaption={t('dashboard.settings.payment_gateways.connect_payment_title', {
      payment: getPaymentProcessorTitle(PaymentProcessorEnum.PaystackCI)
    })} caption={t('dashboard.settings.payment-gateways.paystack_integration')} opened={opened} onClose={() => setOpened(false)} defaultValues={defaultValues} needToast={false} creatable={[{
      field: 'secretToken',
      onRender: (value, onChange) => <FormInput key={'paystack-secret-access-token-create'} label={t('dashboard.settings.payment-gateways.secret_access_token')} value={value} onChange={e => {
        onChange(e.target.value);
        setTempSecretKey(e.target.value);
      }} required error={errors.secretToken} />
    }, {
      field: 'subaccountCode',
      onRender: (value, onChange) => <FormSelect key={'paystack-subaccount-code-create'} label={t('dashboard.settings.payment-gateways.account_title')} value={value} isPreFetching={isSubAccountsFetching} onChange={data => onChange(data)} required error={errors.subaccountCode} data={subAccountSelectData} disabled={!subAccountSelectData} />
    }]} additionalInfo={<HelpCenterLink link={getPaystackHelpPageLink(user?.dashboardLocale)} integration={getPaymentProcessorTitle(PaymentProcessorEnum.PaystackCI)} />} onCreate={onCreate} requiredFields={['secretToken', 'subaccountCode']} />
    </>;
};
export default PaystackCreateModal;